import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { FaDownload, FaShare, FaArrowLeft } from 'react-icons/fa';
import html2pdf from 'html2pdf.js';
import Spinner from '../layout/Spinner';
import Alert from '../layout/Alert';
import FindingsRecommendations from '../reports/FindingsRecommendations';

const FindingsRecommendationsPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation(); // Add this to access query parameters
  
  const [assessment, setAssessment] = useState(null);
  const [findings, setFindings] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [generationTime, setGenerationTime] = useState(0);
  const [generationTimer, setGenerationTimer] = useState(null);
  
  // Define maturity level colors to match the requested color scheme
  const maturityLevelColors = {
    'None': '#333333',       // Dark Gray/Black
    'Initial': '#990000',    // Dark Red
    'Defined': '#E67E22',    // Orange
    'Managed': '#F1C40F',    // Yellow/Gold
    'Compliant': '#2980B9',  // Blue
    'Optimized': '#27AE60'   // Green
  };

  // Function to get color based on score
  const getColorForScore = (score) => {
    if (score < 1) return maturityLevelColors['None'];
    if (score < 20) return maturityLevelColors['Initial'];
    if (score < 50) return maturityLevelColors['Defined'];
    if (score < 75) return maturityLevelColors['Managed'];
    if (score < 91) return maturityLevelColors['Compliant'];
    return maturityLevelColors['Optimized'];
  };

  // Function to get maturity level based on score
  const getMaturityLevelForScore = (score) => {
    if (score < 1) return 'None';
    if (score < 20) return 'Initial';
    if (score < 50) return 'Defined';
    if (score < 75) return 'Managed';
    if (score < 91) return 'Compliant';
    return 'Optimized';
  };
  
  // Get search parameters to check for regenerate flag
  const [searchParams] = useSearchParams();
  const shouldRegenerate = searchParams.get('regenerate') === 'true';
  
  // Fetch assessment data
  useEffect(() => {
    const fetchAssessment = async () => {
      setLoading(true); // Ensure loading is set to true at the start
      setError(''); // Clear any previous errors
      
      try {
        console.log('Fetching assessment data for ID:', id);
        const res = await axios.get(`/api/assessments/${id}`);
        const assessmentData = res.data;
        console.log('Assessment data received:', assessmentData);
        
        // Store the assessment data
        setAssessment(assessmentData);
        
        // Check if assessment is completed
        if (assessmentData.status !== 'completed') {
          setError(t('reports.assessmentNotCompleted'));
          setLoading(false);
          return;
        }
        
        // If regenerate flag is present, always generate new findings
        if (shouldRegenerate) {
          console.log('Regenerate flag detected, generating new findings...');
          await generateFindings(assessmentData);
        } else {
          // Otherwise, try to fetch existing findings first
          try {
            console.log('Attempting to fetch existing findings for assessment ID:', id);
            const findingsRes = await axios.get(`/api/openai/findings/${id}`);
            
            if (findingsRes.data && Object.keys(findingsRes.data).length > 0) {
              console.log('Existing findings found:', findingsRes.data);
              
              // Verify the findings are for the correct assessment
              if (findingsRes.data.assessmentId === id) {
                setFindings(findingsRes.data);
              } else {
                console.log('Findings are for a different assessment, generating new findings...');
                await generateFindings(assessmentData);
              }
            } else {
              console.log('No existing findings found, generating new findings...');
              await generateFindings(assessmentData);
            }
          } catch (err) {
            console.error('Error fetching existing findings:', err);
            console.log('Generating new findings due to error...');
            await generateFindings(assessmentData);
          }
        }
      } catch (err) {
        console.error('Error fetching assessment:', err);
        setError(t('reports.errorFetchingData'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchAssessment();
  }, [id, t, shouldRegenerate]);
  
  // Generate findings and recommendations using OpenAI
  const generateFindings = async (assessmentData) => {
    setGenerating(true);
    setGenerationTime(0);
    
    // Start a timer to count seconds during generation
    const timer = setInterval(() => {
      setGenerationTime(prevTime => prevTime + 1);
    }, 1000);
    
    setGenerationTimer(timer);
    
    try {
      console.log('Generating new findings for assessment ID:', id);
      console.log('Assessment data being used:', assessmentData);
      
      // Make sure we're using the correct assessment ID (MongoDB _id)
      const assessmentId = assessmentData._id || id;
      console.log('Using assessment ID for API call:', assessmentId);
      
      // Add a timestamp to force a fresh request
      const timestamp = new Date().getTime();
      
      // Include assessment data in the request to ensure it's tied to this specific assessment
      const res = await axios.post(`/api/openai/findings/${assessmentId}?_t=${timestamp}`, {
        assessmentData: {
          _id: assessmentId, // Make sure to use _id as that's how it's stored in MongoDB
          id: assessmentId,  // Include both formats to be safe
          title: assessmentData.title,
          score: assessmentData.score,
          categoryScores: assessmentData.categoryScores,
          status: assessmentData.status,
          responses: assessmentData.responses,
          organizationId: assessmentData.organizationId,
          organizationName: assessmentData.organizationName,
          industryType: assessmentData.industryType
        }
      });
      
      console.log('New findings generated:', res.data);
      setFindings(res.data);
    } catch (err) {
      console.error('Error generating findings:', err);
      if (err.response) {
        console.error('Error response data:', err.response.data);
        console.error('Error response status:', err.response.status);
        setError(`${t('reports.errorGeneratingFindings')}: ${err.response.data.msg || err.message}`);
      } else if (err.request) {
        console.error('No response received:', err.request);
        setError(`${t('reports.errorGeneratingFindings')}: No response from server`);
      } else {
        setError(`${t('reports.errorGeneratingFindings')}: ${err.message}`);
      }
    } finally {
      // Clear the timer
      if (generationTimer) {
        clearInterval(generationTimer);
        setGenerationTimer(null);
      }
      setGenerating(false);
    }
  };
  
  // Handle PDF download
  const handleDownload = async () => {
    setLoading(true);
    try {
      // Create a new element for the PDF that includes header and footer
      const tempDiv = document.createElement('div');
      tempDiv.style.padding = '20px';
      
      // Create header with logo
      const header = document.createElement('div');
      header.style.display = 'flex';
      header.style.justifyContent = 'space-between';
      header.style.alignItems = 'center';
      header.style.marginBottom = '20px';
      header.style.borderBottom = '2px solid #D2BA7B';
      header.style.paddingBottom = '15px';
      header.style.backgroundColor = '#FFFAFA';
      
      const headerLeft = document.createElement('div');
      const title = document.createElement('h2');
      title.style.color = '#243444';
      title.style.margin = '0';
      title.style.fontWeight = 'bold';
      title.textContent = t('reports.findingsRecommendations');
      
      const subtitle = document.createElement('p');
      subtitle.style.color = '#4C5454';
      subtitle.style.margin = '5px 0 0 0';
      subtitle.textContent = assessment?.title || t('reports.maturityAssessment');
      
      headerLeft.appendChild(title);
      headerLeft.appendChild(subtitle);
      
      const headerRight = document.createElement('div');
      headerRight.style.textAlign = 'right';
      
      const logo = document.createElement('img');
      // Use window.location.origin to create an absolute URL for the logo
      logo.src = `${window.location.origin}/images/Navigator Logo Transparent.png`;
      logo.alt = 'Business Resilience Navigator Logo';
      logo.style.maxWidth = '180px';
      logo.style.height = 'auto';
      // Set crossOrigin to anonymous to avoid CORS issues
      logo.crossOrigin = 'anonymous';
      
      headerRight.appendChild(logo);
      
      header.appendChild(headerLeft);
      header.appendChild(headerRight);
      
      // Add header to the temp div
      tempDiv.appendChild(header);
      
      // Get the original content to convert to PDF
      const element = document.getElementById('findings-recommendations-content');
      
      // Clone the content and add it to our temp div
      const contentClone = element.cloneNode(true);
      tempDiv.appendChild(contentClone);
      
      // Create footer
      const footer = document.createElement('div');
      footer.style.marginTop = '50px';
      footer.style.borderTop = '2px solid #D2BA7B';
      footer.style.paddingTop = '20px';
      footer.style.paddingBottom = '20px';
      footer.style.textAlign = 'center';
      footer.style.backgroundColor = '#243444';
      footer.style.color = '#FFFAFA';
      footer.style.fontSize = '14px';
      footer.style.borderRadius = '0 0 8px 8px';
      
      const footerText = document.createElement('p');
      footerText.style.margin = '0';
      footerText.innerHTML = `&copy; ${new Date().getFullYear()} Business Resilience Navigator. All rights reserved.`;
      
      footer.appendChild(footerText);
      
      // Add footer to the temp div
      tempDiv.appendChild(footer);
      
      // Use the temp div instead of the original element
      document.body.appendChild(tempDiv);
      tempDiv.style.display = 'none';
      
      if (!element) {
        throw new Error('Content element not found');
      }
      
      const opt = {
        margin: [1, 1, 2, 1], // Increased bottom margin for footer
        filename: `Findings_Recommendations_${assessment?.title ? assessment.title.replace(/\s+/g, '_') : 'Report'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'cm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };
      
      // Use our custom tempDiv with header and footer
      const worker = html2pdf().from(tempDiv).set(opt);
      
      // Add page numbers to each page
      worker.toPdf().get('pdf').then((pdf) => {
        // Get total page count
        const totalPages = pdf.internal.getNumberOfPages();
        
        // Add page numbers to each page
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          
          // Add page number
          pdf.setFontSize(10);
          pdf.setTextColor(76, 84, 84); // #4C5454
          
          // Add page number
          pdf.text(
            `Page ${i} of ${totalPages}`,
            pdf.internal.pageSize.getWidth() - 2,
            pdf.internal.pageSize.getHeight() - 1,
            { align: 'right' }
          );
        }
      });
      
      // Save the PDF
      worker.save().then(() => {
        // Clean up - remove the temp div from the document
        document.body.removeChild(tempDiv);
        setLoading(false);
      }).catch(err => {
        console.error('Error generating PDF:', err);
        // Clean up - remove the temp div from the document
        if (document.body.contains(tempDiv)) {
          document.body.removeChild(tempDiv);
        }
        setError(t('reports.errorDownloadingPDF') + ': ' + (err.message || 'Unknown error'));
        setLoading(false);
        alert('Failed to generate PDF. Please try again later.');
      });
    } catch (err) {
      console.error('Error downloading PDF:', err);
      // No need to clean up tempDiv here as it would be out of scope
      // if we get an error before it's created
      setError(t('reports.errorDownloadingPDF') + ': ' + (err.message || 'Unknown error'));
      setLoading(false);
    }
  };
  
  // Handle share functionality
  const handleShare = () => {
    alert(t('common.featureComingSoon'));
  };
  
  if (loading && !assessment) {
    return <Spinner />;
  }
  
  if (generating) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div className="page-title-section">
            <button className="back-button" onClick={() => navigate(-1)}>
              <FaArrowLeft /> {t('common.back')}
            </button>
            <h1 className="page-title">{t('reports.findingsRecommendations')}</h1>
          </div>
        </div>
        <div className="generating-container" style={{ 
          textAlign: 'center', 
          padding: '50px 20px',
          backgroundColor: '#FFFAFA',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          maxWidth: '600px',
          margin: '50px auto'
        }}>
          <Spinner />
          <h2 style={{ color: '#243444', marginTop: '20px', marginBottom: '10px' }}>
            Generating Findings & Recommendations
          </h2>
          <div style={{ 
            backgroundColor: '#243444', 
            color: '#D2BA7B', 
            padding: '15px', 
            borderRadius: '5px',
            fontSize: '24px',
            fontWeight: 'bold',
            marginTop: '20px',
            marginBottom: '20px',
            display: 'inline-block',
            minWidth: '120px'
          }}>
            {generationTime}s
          </div>
          <p style={{ color: '#4C5454', marginTop: '20px' }}>
            This process may take 1-2 minutes as we analyze all categories in your assessment.
          </p>
          <p style={{ color: '#4C5454', marginTop: '10px' }}>
            We're using AI to generate personalized findings and recommendations based on your assessment responses.
          </p>
          <div style={{ marginTop: '30px', fontSize: '14px', color: '#7C1227' }}>
            Please do not refresh the page during generation.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title-section">
          <button className="back-button" onClick={() => navigate(-1)}>
            <FaArrowLeft /> {t('common.back')}
          </button>
          <h1 className="page-title">{t('reports.findingsRecommendations')}</h1>
        </div>
        <div className="page-actions">
          <button className="action-button" onClick={handleDownload}>
            <FaDownload /> {t('reports.downloadPDF')}
          </button>
          <button className="action-button" onClick={handleShare}>
            <FaShare /> {t('reports.share')}
          </button>
        </div>
      </div>
      
      {/* Show spinner when loading assessment or generating findings */}
      {(loading || generating) ? (
        <div className="loading-container" style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spinner />
          <p style={{ marginTop: '20px', color: '#243444', fontWeight: 'bold', fontSize: '18px' }}>
            {generating ? 'Generating Findings & Recommendations...' : 'Loading Report...'}
          </p>
          {generating && (
            <>
              <p style={{ marginTop: '10px', color: '#4C5454' }}>
                This may take a minute as we analyze all categories in your assessment.
              </p>
              <p style={{ marginTop: '5px', color: '#243444', fontWeight: 'bold' }}>
                Time elapsed: {generationTime} seconds
              </p>
            </>
          )}
        </div>
      ) : error ? (
        <Alert type="danger" message={error} />
      ) : (
        <div id="findings-recommendations-content" className="report-content">
          {console.log('Rendering FindingsRecommendations with:', { assessment, findings })}
          <FindingsRecommendations assessment={assessment} findings={findings} />
        </div>
      )}
    </div>
  );
};

export default FindingsRecommendationsPage;
